/* eslint-disable @typescript-eslint/indent */
import Image from 'next/image';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import ProductData from '@customTypes/product';
import { getDetailedDate, parseMilitaryTime } from '@helpers/date';
import Currency from '@components/common/Currrency/Currency';
import arrowRight from '@assets/images/arrow-right.svg';
import clockIcon from '@assets/images/clock.svg';
import clockUnavailableIcon from '@assets/images/clock-unavailable.svg';
import closeArrow from '@assets/images/close-arrows.svg';
import ButtonPrimary from '@components/common/ButtonPrimary';
import useIsMobile from '@hooks/useIsMobile';
import {
  isProductUnAvailable as isUnAvailable,
  numberOfAvailableUnits,
  showAvailabilityWarning,
} from '@helpers/product';
import { CartItemDetails } from '@customTypes/cart-items';
import { timeSelected } from '@events/timeSlottedProduct';
import useSessionStorage from '@hooks/useSessionStorage';
import sessionStorageKeys from '@constants/sessionStorageKeys';
import HotelDetails from '@customTypes/hotel-details';
import useHotSpot from '@hooks/useHotSpot';
import { FAIR_PRICING_VARIANT } from '@constants/fairPricing';
import { getProductPriceForGuestType } from '@utils/fairPricing';
import useSoldOut from '@hooks/useSoldOut';
import Button from '@solar/common/Button/ButtonV2';
import {
  AMPLITUDE_EXPERIMENT_CONTROL_NAME,
  AMPLITUDE_EXPERIMENT_VARIANT_NAME,
} from '@constants/amplitudeExperiments';
import { useFeatureFlagContext } from '@context/FeatureFlagContext';

type Props = {
  cartData: CartItemDetails | undefined;
  date: Date | undefined;
  hotel: HotelDetails;
  product: ProductData;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  setStep: Dispatch<SetStateAction<number>>;
  setSelectedTimeSlottedProduct: Dispatch<SetStateAction<ProductData | undefined>>;
  showFairPricing: boolean;
};

export default function TimeSlotSelector({
  cartData,
  date,
  hotel,
  product,
  setIsOpen,
  setStep,
  setSelectedTimeSlottedProduct,
  showFairPricing,
}: Props) {
  const [filteredProducts, setFilteredProducts] = useState<ProductData[]>([]);

  const { applyHotSpotDiscountToTotal, isDateWithinVisitWindow } = useHotSpot();
  const isMobile = useIsMobile();
  const { getItem } = useSessionStorage();
  const { getSoldOutTextForProduct } = useSoldOut();
  const { ctasUpdateExperiment } = useFeatureFlagContext();

  const isProductUnAvailable = (productData: ProductData) => isUnAvailable(productData, cartData);

  const handleSelectTime = (timeSlottedProduct: ProductData) => {
    // Event
    timeSelected(timeSlottedProduct);

    setSelectedTimeSlottedProduct(timeSlottedProduct);
    setStep(1);
  };

  /**
   * It generates a date-time based on selected_date (from session storage) and provided time.
   * @param time to generate the date-time from. ex: "19:00:00+00" | "10:00:00"
   * @returns a Date object
   */
  const generateDateTime = (time: string | undefined) => {
    const parsedTime = time?.replace('+00', '');

    return new Date(`${getItem(sessionStorageKeys.SMART_CALENDAR_DATE)} ${parsedTime}`);
  };

  /**
   * It uses generateDateTime() function to parse and creates a new date from provided time, and
   * returns only the time as a number.
   * @param time to generate the date-time from. ex: "19:00:00+00" | "10:00:00"
   * @returns time as number - ex: 1664458625991
   */
  const getTimeAsNumber = (time: string | undefined) => {
    if (!time) return 0;

    const dateTime = generateDateTime(time);

    return dateTime.getTime();
  };

  useEffect(() => {
    setFilteredProducts(product.children ?? []);
  }, [product.children]);

  return (
    <div className="flex flex-col items-center justify-start max-h-640px overflow-hidden rounded-xl w-full">
      <div className="h-74px flex justify-between items-center w-full bg-white z-100 rounded-t-xl">
        <button
          type="button"
          className="absolute flex left-26px top-28px"
          onClick={() => setIsOpen(false)}
        >
          <Image src={closeArrow} width={16} height={16} alt="close" />
        </button>

        <div className="border-b flex flex-col items-center justify-center py-4 rounded-t-xl w-full">
          <p className="font-rp-pn-semi-bold leading-19px text-base text-center">{product.name}</p>
          {date && <p className="mt-1 leading-17px opacity-70 text-sm">{getDetailedDate(date)}</p>}
        </div>
      </div>

      <div className="overflow-y-scroll w-full d:mb-0">
        <p className="font-rp-pn-semi-bold leading-40px mt-30px mx-22px -tracking-0.5  text-2xl text-rp-primary-black-medium d:leading-29px d:mx-10 d:text-28 d:-tracking-0.24">
          Select a time
        </p>

        <div className="mt-4 overflow-auto pb-4 pt-2 px-22px d:pb-10 d:px-10">
          {product.children && filteredProducts.length > 0 ? (
            filteredProducts
              .sort((a, b) => getTimeAsNumber(a.start_time) - getTimeAsNumber(b.start_time))
              .map((timeSlottedProduct: ProductData, index: number, { length }) => (
                <div
                  key={timeSlottedProduct.product_id}
                  role="button"
                  tabIndex={0}
                  className={`${index > 0 && 'mt-4'} ${
                    isProductUnAvailable(timeSlottedProduct)
                      ? 'bg-rp-light-gray-2 d:border-none'
                      : 'border-rp-gray-divider d:border-rp-gray-border2'
                  } ${
                    !isMobile && length - 1 === index ? 'mb-4' : ''
                  } border flex h-82px items-center justify-between px-4 rounded-xl shadow-select w-full d:cursor-default d:shadow-none`}
                  onClick={() => {
                    // remove clicking behavior on desktop in favor of "Select" button
                    // or in case the product is unavailable
                    if (!isMobile || isProductUnAvailable(timeSlottedProduct)) return;

                    handleSelectTime(timeSlottedProduct);
                  }}
                  onKeyDown={(e) => {
                    // remove clicking behavior on desktop in favor of "Select" button
                    // or in case the product is unavailable
                    if (!isMobile || isProductUnAvailable(timeSlottedProduct)) return;

                    if (e.key === 'Enter') {
                      handleSelectTime(timeSlottedProduct);
                    }
                  }}
                >
                  <div className="flex items-center justify-center d:justify-start">
                    <div className="flex">
                      <Image
                        src={
                          isProductUnAvailable(timeSlottedProduct)
                            ? clockUnavailableIcon
                            : clockIcon
                        }
                        width={40}
                        height={40}
                        alt={
                          isProductUnAvailable(timeSlottedProduct) ? 'unavailable' : 'select a time'
                        }
                      />
                    </div>

                    <div className="font-rp-pn-semi-bold ml-4">
                      <p
                        className={`${
                          isProductUnAvailable(timeSlottedProduct) ? 'opacity-50' : ''
                        } text-17`}
                      >
                        {parseMilitaryTime(timeSlottedProduct.start_time)} -{' '}
                        {parseMilitaryTime(timeSlottedProduct.end_time)}
                      </p>

                      {isProductUnAvailable(timeSlottedProduct) ? (
                        isMobile && (
                          <p className="font-rp-pn-semi-bold tracking-0.3 text-rp-secondary-red text-13 uppercase">
                            {getSoldOutTextForProduct(timeSlottedProduct)}
                          </p>
                        )
                      ) : (
                        <div className="flex items-baseline">
                          {isMobile && (
                            <Currency
                              value={timeSlottedProduct.adult_price}
                              valueClasses="font-rp-pn-regular leading-6 mr-2 text-17"
                              precision={0}
                            />
                          )}

                          {date && showAvailabilityWarning(timeSlottedProduct, cartData) && (
                            <p className="tracking-0.3 text-rp-gold text-13">
                              {`ONLY ${numberOfAvailableUnits(timeSlottedProduct, cartData)} LEFT`}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Show next when there is availability,
              as an arrow on mobiles or as a Select button on desktop */}
                  {isProductUnAvailable(timeSlottedProduct) ? (
                    !isMobile && (
                      <p className="font-rp-pn-semi-bold mr-6 tracking-0.3 text-rp-secondary-red text-13 uppercase">
                        {getSoldOutTextForProduct(timeSlottedProduct)}
                      </p>
                    )
                  ) : (
                    <div className="flex items-center justify-center">
                      {isMobile ? (
                        <Image src={arrowRight} width={8} height={16} alt="select time slot" />
                      ) : (
                        <div className="flex items-center space-x-4">
                          <div className="w-full">
                            <div className="flex flex-row items-center space-x-2">
                              {date &&
                              timeSlottedProduct.is_hot_spot &&
                              hotel.hot_spot &&
                              isDateWithinVisitWindow(date, hotel) ? (
                                <Currency
                                  value={applyHotSpotDiscountToTotal(
                                    +timeSlottedProduct.adult_price,
                                    hotel.hot_spot,
                                    showFairPricing,
                                  )}
                                  valueClasses="leading-4 text-rp-hot-spot text-md"
                                />
                              ) : null}

                              <Currency
                                value={getProductPriceForGuestType(
                                  timeSlottedProduct,
                                  'adult',
                                  showFairPricing,
                                )}
                                valueClasses={`leading-4 ${
                                  date &&
                                  timeSlottedProduct.is_hot_spot &&
                                  isDateWithinVisitWindow(date, hotel)
                                    ? 'text-rp-disabled text-base line-through'
                                    : 'text-custom-black text-md'
                                }`}
                              />
                            </div>

                            {!(date && isProductUnAvailable(timeSlottedProduct)) &&
                            showFairPricing ? (
                              <span className="font-medium font-rp-pn-regular flex-shrink-0 leading-11px text-rp-gray-2 text-12">
                                {FAIR_PRICING_VARIANT === 'variant1'
                                  ? 'Includes fees'
                                  : `+$${(+getProductPriceForGuestType(
                                      timeSlottedProduct,
                                      'adult',
                                      showFairPricing,
                                    )).toFixed(0)} in fees`}
                              </span>
                            ) : null}
                          </div>

                          {!ctasUpdateExperiment ||
                          ctasUpdateExperiment === AMPLITUDE_EXPERIMENT_CONTROL_NAME ? (
                            <div className="w-132px">
                              <ButtonPrimary
                                onClick={() => handleSelectTime(timeSlottedProduct)}
                                classes="px-11 pt-2 pb-2"
                              >
                                Select
                              </ButtonPrimary>
                            </div>
                          ) : (
                            <div className="flex-shrink-0">
                              <Button
                                variant={
                                  ctasUpdateExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME
                                    ? 'primary'
                                    : 'ultramarine'
                                }
                                onClick={() => handleSelectTime(timeSlottedProduct)}
                              >
                                Select
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
          ) : (
            <p className="d:mb-8">
              The time-slots for today have already passed. Please select a date in the future to
              proceed.
            </p>
          )}
        </div>

        {/* Desktop scroll blur */}
        {!isMobile && (
          <>
            <div className="absolute bottom-10 px-10 w-full">
              <div className="bg-blur border-b border-rp-gray-divider h-10 z-10" />
            </div>
            <div className="absolute bg-white bottom-0 h-10 px-10 rounded-b-xl w-full z-10" />
          </>
        )}
      </div>
    </div>
  );
}
